import { Component, OnInit, AfterViewInit, TemplateRef, ViewChild, Input, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { fadeInOut } from '../../../services/animations';
import { AlertService, DialogType, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from "../../../services/app-translation.service";
import { Utilities } from "../../../services/utilities";
import { ListViewViewModel } from '../../model/listViewViewModel';
import { Configuration, AspectViewModel, AspectService, AspectGroupService, AspectGroupViewModel } from '../../index';
import { Router, ActivatedRoute, NavigationExtras } from '@angular/router';
import { Observable } from 'rxjs';
import { Permission } from '../../../models/permission.model';
import { AuthService } from '../../../services/auth.service';

@Component({
    selector: 'app-aspect-list',
    templateUrl: './aspect-list.component.html',
    styleUrls: ['./aspect-list.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AspectListComponent implements OnInit, AfterViewInit {
    @ViewChild(MatPaginator, { static: true })
    paginator: MatPaginator;
    @ViewChild(MatSort, { static: true })
    sort: MatSort;
    aspectGroupId: number = null;

    displayedColumns = ['name', 'enabled', 'createdDate', 'updatedDate', 'actions'];
    dataSource: MatTableDataSource<AspectViewModel>;
    loadingIndicator: boolean;

    // Filtering
    @Input() fixedView: boolean = false;
    currentSearchText: string;
    pageTitle: string = "Aspekter för aspektgrupp";

    constructor(private alertService: AlertService,
        private translationService: AppTranslationService,
        private authService: AuthService,
        private aspectGroupService: AspectGroupService,
        private aspectService: AspectService,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private router: Router,
        private route: ActivatedRoute) {

        this.dataSource = new MatTableDataSource<AspectViewModel>();
        this.currentSearchText = "";
        this.aspectGroupId = Number.parseInt(this.route.snapshot.paramMap.get('aspectGroupId'));
    }

    ngOnInit() {
        this.loadData();
    }

    ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    public applyFilter(filterValue: string) {
        this.dataSource.filter = filterValue;
    }

    private refresh() {
        // Causes the filter to refresh there by updating with recently added data.
        this.applyFilter(this.dataSource.filter);
    }

    public loadData() {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;

        this.aspectService.getAspectsByAspectGroup(this.aspectGroupId).subscribe(
            bus => this.onDataLoadSuccessful(bus),
            error => this.onDataLoadFailed(error)
        );

        this.aspectGroupService.getAspectGroupById(this.aspectGroupId).subscribe(
            bus => this.onAspectGroupDataLoadSuccessful(bus),
            error => this.onDataLoadFailed(error)
        );

    }

    private onDataLoadSuccessful(aspects: AspectViewModel[]) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;
        this.dataSource.data = aspects;
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"), this.translationService.getTranslation("FetchErrorMessage") + "\r\n" + Utilities.getHttpResponseMessage(error),
            MessageSeverity.error, error);
    }


    confirmDelete(aspect: AspectViewModel) {
        let message: string = this.translationService.getTranslation("commands.Delete") +
            " " +
            aspect.name +
            "?";
        let action: string = this.translationService.getTranslation("commands.Delete").toUpperCase();

        this.snackBar.open(message, action, { duration: 5000 })
            .onAction().subscribe(() => {
                this.alertService.startLoadingMessage(
                    this.translationService.getTranslation("globalTexts.DeletingMessage"));
                this.loadingIndicator = true;

                this.aspectService.deleteAspect(aspect.aspectId)
                    .subscribe(results => {
                        this.alertService.stopLoadingMessage();
                        this.loadingIndicator = false;
                        this.alertService.showMessage(this.translationService.getTranslation("commands.Delete"),
                            aspect.name + " " + this.translationService.getTranslation("globalTexts.deleted"),
                            MessageSeverity.success);
                        this.loadData();
                    },
                    error => {
                        this.alertService.stopLoadingMessage();
                        this.loadingIndicator = false;

                        let errorMessage = this.translationService.getTranslation("globalTexts.Error") +
                            ": " +
                            Utilities.getHttpResponseMessage(error);

                        this.alertService.showStickyMessage(
                            this.translationService.getTranslation("globalTexts.DeleteError"),
                            errorMessage,
                            MessageSeverity.error,
                            error);
                    });
            });
    }

    gotoAspectDetails(): void {
        if (this.aspectGroupId) {
            this.router.navigate(['./aspect'], { queryParams: { aspectGroupId: this.aspectGroupId } });
        } else {
            this.router.navigate(['./aspect']);
        }
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.manageModel);
    }

    hasPermissionToDelete(): boolean {
        return this.authService.userHasPermission(Permission.manageModel);
    }

    onAspectGroupDataLoadSuccessful(aspectGroupViewModel: AspectGroupViewModel): void {
        this.pageTitle = "Aspekter för aspektgrupp - " + aspectGroupViewModel.name;
    }
}
